import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useLiveMatch } from '../LiveMatchContext.tsx';

function useClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);
  const date = now.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
  const time = now.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  return `${date} · ${time}`;
}

interface Props {
  swapped?: boolean;
}

export default function ScoreBoard({ swapped = false }: Props) {
  const { state } = useLiveMatch();
  const clockLabel = useClock();
  const { currentSetScore, setsWon, completedSets, servingTeamNow, strategy, currentSet } = state;

  const setsToWin = strategy === 'BEST_OF_5' ? 3 : 2;

  // Tutti i valori derivano da quale squadra è a sinistra
  const leftIsHome   = !swapped;
  const leftName     = leftIsHome ? state.homeTeamName    : state.awayTeamName;
  const rightName    = leftIsHome ? state.awayTeamName    : state.homeTeamName;
  const leftScore    = leftIsHome ? currentSetScore.homePoints  : currentSetScore.awayPoints;
  const rightScore   = leftIsHome ? currentSetScore.awayPoints  : currentSetScore.homePoints;
  const leftWon      = leftIsHome ? setsWon.home  : setsWon.away;
  const rightWon     = leftIsHome ? setsWon.away  : setsWon.home;
  const leftColor    = leftIsHome ? '#4fc3f7' : '#ef9a9a';
  const rightColor   = leftIsHome ? '#ef9a9a' : '#4fc3f7';
  const leftServing  = servingTeamNow === (leftIsHome ? 'HOME' : 'AWAY');
  const rightServing = servingTeamNow === (leftIsHome ? 'AWAY' : 'HOME');

  const pulseKeyframes = {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.4 },
  };

  return (
    <Box
      sx={{
        bgcolor: '#1a1a2e',
        color: 'white',
        px: 2,
        pt: 1.5,
        pb: 1,
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: '1fr 1.4fr 1fr',
        alignItems: 'center',
        gap: 1,
      }}
    >
      {/* ── Orologio (più piccolo) ─────────────────── */}
      <Typography
        sx={{
          position: 'absolute',
          top: 6,
          right: 10,
          fontSize: { xs: '0.85rem', sm: '1rem', md: '1.15rem' },
          fontWeight: 600,
          color: '#FFD700',
          opacity: 0.82,
          letterSpacing: '0.03em',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {clockLabel}
      </Typography>

      {/* ── SINISTRA ───────────────────────────────── */}
      <Box sx={{ textAlign: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, mb: 0.5 }}>
          {leftServing && (
            <Typography
              sx={{
                fontSize: '1.3rem',
                lineHeight: 1,
                animation: 'scoreboard-pulse 1.2s ease-in-out infinite',
                '@keyframes scoreboard-pulse': pulseKeyframes,
              }}
            >
              🏐
            </Typography>
          )}
          <Typography
            noWrap
            sx={{ fontWeight: 700, opacity: 0.9, fontSize: { xs: '1rem', sm: '1.4rem', md: '1.8rem' } }}
          >
            {leftName}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.25, mt: 0.5 }}>
          {Array.from({ length: setsToWin }, (_, i) => (
            <Box
              key={i}
              sx={{
                width: { xs: 14, sm: 18, md: 22 },
                height: { xs: 14, sm: 18, md: 22 },
                borderRadius: '50%',
                bgcolor: i < leftWon ? leftColor : 'rgba(255,255,255,0.18)',
                transition: 'background-color 0.3s',
              }}
            />
          ))}
        </Box>
      </Box>

      {/* ── PUNTEGGIO ──────────────────────────────── */}
      <Box sx={{ textAlign: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center' }}>
          <Typography
            key={`L${leftScore}`}
            sx={{
              fontSize: 'clamp(5rem, 18vw, 22rem)',
              fontWeight: 900,
              lineHeight: 0.9,
              color: '#FFD700',
              fontVariantNumeric: 'tabular-nums',
              minWidth: '2ch',
              textAlign: 'right',
              animation: 'score-flash 0.55s ease-out',
              '@keyframes score-flash': {
                '0%':   { transform: 'scale(1.3)',  filter: 'brightness(3)', color: '#66BB6A' },
                '55%':  { transform: 'scale(1.1)',  filter: 'brightness(1.5)' },
                '100%': { transform: 'scale(1)',    filter: 'brightness(1)',  color: '#FFD700' },
              },
            }}
          >
            {leftScore}
          </Typography>
          <Typography
            sx={{
              fontSize: 'clamp(2rem, 7vw, 9rem)',
              fontWeight: 300,
              lineHeight: 0.9,
              color: 'rgba(255,255,255,0.3)',
              mx: { xs: 0.5, sm: 1.5 },
            }}
          >
            –
          </Typography>
          <Typography
            key={`R${rightScore}`}
            sx={{
              fontSize: 'clamp(5rem, 18vw, 22rem)',
              fontWeight: 900,
              lineHeight: 0.9,
              color: '#FFD700',
              fontVariantNumeric: 'tabular-nums',
              minWidth: '2ch',
              textAlign: 'left',
              animation: 'score-flash 0.55s ease-out',
              '@keyframes score-flash': {
                '0%':   { transform: 'scale(1.3)',  filter: 'brightness(3)', color: '#66BB6A' },
                '55%':  { transform: 'scale(1.1)',  filter: 'brightness(1.5)' },
                '100%': { transform: 'scale(1)',    filter: 'brightness(1)',  color: '#FFD700' },
              },
            }}
          >
            {rightScore}
          </Typography>
        </Box>

        {/* Set corrente + precedenti */}
        <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
          <Typography variant="caption" sx={{ opacity: 0.45, fontSize: { xs: 10, sm: 12 } }}>
            Set {currentSet}
          </Typography>
          {completedSets.map((s) => {
            const lScore = leftIsHome ? s.homePoints : s.awayPoints;
            const rScore = leftIsHome ? s.awayPoints : s.homePoints;
            return (
              <Typography key={s.setNumber} variant="caption" sx={{ opacity: 0.6, fontSize: { xs: 10, sm: 12 } }}>
                <span style={{ color: lScore > rScore ? leftColor : undefined }}>{lScore}</span>
                {'-'}
                <span style={{ color: rScore > lScore ? rightColor : undefined }}>{rScore}</span>
              </Typography>
            );
          })}
        </Box>
      </Box>

      {/* ── DESTRA ─────────────────────────────────── */}
      <Box sx={{ textAlign: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, mb: 0.5 }}>
          <Typography
            noWrap
            sx={{ fontWeight: 700, opacity: 0.9, fontSize: { xs: '1rem', sm: '1.4rem', md: '1.8rem' } }}
          >
            {rightName}
          </Typography>
          {rightServing && (
            <Typography
              sx={{
                fontSize: '1.3rem',
                lineHeight: 1,
                animation: 'scoreboard-pulse 1.2s ease-in-out infinite',
              }}
            >
              🏐
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.25, mt: 0.5 }}>
          {Array.from({ length: setsToWin }, (_, i) => (
            <Box
              key={i}
              sx={{
                width: { xs: 14, sm: 18, md: 22 },
                height: { xs: 14, sm: 18, md: 22 },
                borderRadius: '50%',
                bgcolor: i < rightWon ? rightColor : 'rgba(255,255,255,0.18)',
                transition: 'background-color 0.3s',
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
}
